import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useCallback } from 'react';
import { ScrollArea } from '../../ui/scroll-area';
import MessagesEmpty from './MessagesEmpty';
import IndividualMessage from './IndividualMessage';
import { LoadingSpinner } from '../../ui/loading-spinner';
import UserChatInput from './UserChatInput';
import { getEmailContext } from '../../lib/outlook';
import { useChatContext } from './ChatContext';
import { Mail, PenSquare, ArrowLeft, FileSearch } from 'lucide-react';
import { ProfileDropdown } from '../playbook/ProfileDropdown';
import { SignalRStatusDot } from '../SignalRStatusDot';
import { useUserId } from '../../lib/useUserId';
import OAuthConnectWidget from '../oauth/OAuthConnectWidget';
import NewEmailDraftForm from './NewEmailDraftForm';
import ReadModeReplyForm from './ReadModeReplyForm';
import CustomReadView from './CustomReadView';
import { Button } from '@airia/ui';
const OutlookChat = ({ isComposeMode = false, customReadViewAgentId = null, }) => {
    const { messages, sendMessage, sendButtonDisabled } = useChatContext();
    const { userId } = useUserId();
    const [inputText, setInputText] = React.useState('');
    const scrollAreaRef = React.useRef(null);
    const [isRewriteMode, setIsRewriteMode] = React.useState(isComposeMode); // Start in rewrite mode only for compose
    const [isReplyDraftMode, setIsReplyDraftMode] = React.useState(!isComposeMode && !customReadViewAgentId); // Start in reply draft mode for read mode (unless custom view)
    const [isDraftingNewEmail, setIsDraftingNewEmail] = React.useState(false);
    const [showCustomReadView, setShowCustomReadView] = React.useState(!isComposeMode && !!customReadViewAgentId); // Start in custom view if available and in read mode
    const responseCallbackRef = React.useRef(null);
    const lastProcessedIdRef = React.useRef(null);
    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (scrollAreaRef.current) {
            const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }
    }, []);
    // Watch for new messages and call response callback when pending
    // This is used by RewriteEmailForm (compose mode), ReadModeReplyForm, and NewEmailDraftForm
    useEffect(() => {
        // Wait until streaming is complete before calling callback
        if (responseCallbackRef.current && messages.length > 0 && !sendButtonDisabled) {
            const lastMessage = messages[messages.length - 1];
            // Only process if it's an assistant message and we haven't processed this ID yet
            if (lastMessage?.role === 'assistant' &&
                lastMessage.id !== lastProcessedIdRef.current) {
                lastProcessedIdRef.current = lastMessage.id;
                responseCallbackRef.current(lastMessage.value);
                responseCallbackRef.current = null; // Clear callback after use
            }
        }
    }, [messages, sendButtonDisabled]);
    const onMessageSend = async (e) => {
        e.preventDefault();
        setInputText('');
        // Exit draft modes when user starts chatting
        setIsRewriteMode(false);
        setIsReplyDraftMode(false);
        try {
            if (!userId) {
                console.error('User ID not available');
                return;
            }
            const emailContext = await getEmailContext();
            await sendMessage({
                userInput: inputText,
                userId,
                inMemoryMessages: [
                    {
                        role: 'user',
                        message: emailContext,
                    },
                    ...messages.map((m) => ({
                        role: m.role,
                        message: m.value,
                    })),
                ],
            });
        }
        catch (error) {
            console.error(error);
        }
    };
    // Handler for draft forms that keeps form visible and uses callback for response
    const handleGenerateRewrite = useCallback(async (prompt, callback) => {
        try {
            if (!userId) {
                console.error('User ID not available');
                return;
            }
            // Store callback to be called when response arrives
            responseCallbackRef.current = callback;
            const emailContext = await getEmailContext();
            // Send message - stay in rewrite mode
            await sendMessage({
                userInput: prompt,
                userId,
                inMemoryMessages: [
                    {
                        role: 'user',
                        message: emailContext,
                    },
                ],
            });
        }
        catch (error) {
            console.error(error);
            responseCallbackRef.current = null; // Clear on error
        }
        // Deliberately omits 'messages' from dependencies - rewrite uses only current email context,
        // not previous message history. This is intentional to keep rewrites independent.
    }, [userId, sendMessage]);
    // Show custom read view if available and active
    if (showCustomReadView && customReadViewAgentId && !isComposeMode) {
        return (_jsx("div", { className: "relative w-full h-full flex flex-col bg-gradient-to-br from-background to-muted/20", children: _jsx(CustomReadView, { agentId: customReadViewAgentId, onSwitchToReplyMode: () => {
                    setShowCustomReadView(false);
                    setIsReplyDraftMode(true);
                } }) }));
    }
    return (_jsxs("div", { className: "relative w-full h-full flex flex-col bg-gradient-to-br from-background to-muted/20", children: [_jsx("div", { className: "flex-shrink-0 p-4 pb-2", children: _jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [!isDraftingNewEmail && !isRewriteMode && !isReplyDraftMode && (_jsx(Button, { variant: "ghost", size: "icon", onClick: () => {
                                        if (isComposeMode) {
                                            setIsRewriteMode(true);
                                        }
                                        else if (customReadViewAgentId) {
                                            // Go back to custom read view if available
                                            setShowCustomReadView(true);
                                        }
                                        else {
                                            setIsReplyDraftMode(true);
                                        }
                                    }, title: customReadViewAgentId
                                        ? 'Back to analysis'
                                        : 'Back to draft form', className: "h-9 w-9", children: _jsx(ArrowLeft, { className: "size-4" }) })), _jsx("div", { className: "p-2 bg-primary/10 rounded-full", children: _jsx(Mail, { className: "size-4 text-primary" }) }), _jsxs("div", { children: [_jsx("h2", { className: "text-sm font-medium text-foreground", children: isComposeMode || isDraftingNewEmail ? 'Draft Mode' : 'Reply Mode' }), _jsx("p", { className: "text-xs text-muted-foreground", children: isComposeMode || isDraftingNewEmail
                                                ? 'Compose or rewrite your email'
                                                : 'Draft a reply to this email' })] })] }), _jsxs("div", { className: "flex items-center gap-1", children: [!isComposeMode && customReadViewAgentId && (_jsx(Button, { variant: "ghost", size: "icon", onClick: () => setShowCustomReadView(true), title: "View custom analysis", className: "h-9 w-9", children: _jsx(FileSearch, { className: "size-4" }) })), !isDraftingNewEmail && (_jsx(Button, { variant: "ghost", size: "icon", onClick: () => setIsDraftingNewEmail(true), title: "Draft new email", className: "h-9 w-9", children: _jsx(PenSquare, { className: "size-4" }) })), _jsx(SignalRStatusDot, {}), _jsx(ProfileDropdown, {})] })] }) }), _jsx("div", { className: "flex-1 overflow-hidden", children: _jsx(ScrollArea, { ref: scrollAreaRef, className: "h-full px-4", children: _jsxs("div", { className: "pb-4", children: [_jsx("div", { className: "mb-4", children: _jsx(OAuthConnectWidget, {}) }), isDraftingNewEmail && (_jsx(NewEmailDraftForm, { onBack: () => setIsDraftingNewEmail(false), onGenerate: handleGenerateRewrite })), !isDraftingNewEmail && isComposeMode && (_jsx("div", { className: isRewriteMode ? '' : 'hidden', children: _jsx(MessagesEmpty, { onGenerateRewrite: handleGenerateRewrite, isComposeMode: isComposeMode }) })), !isDraftingNewEmail && !isComposeMode && (_jsx("div", { className: isReplyDraftMode ? '' : 'hidden', children: _jsx(ReadModeReplyForm, { onGenerate: handleGenerateRewrite }) })), !isDraftingNewEmail &&
                                !isRewriteMode &&
                                isComposeMode &&
                                messages.length <= 0 && _jsx(MessagesEmpty, { isComposeMode: isComposeMode }), !isDraftingNewEmail &&
                                !isRewriteMode &&
                                !isReplyDraftMode &&
                                messages.length > 0 && (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(IndividualMessage, { message: {
                                            role: 'assistant',
                                            value: 'How can I help you with this email?',
                                            id: 'greeting',
                                        } }), messages.map((message) => (_jsx(IndividualMessage, { message: message }, message.id)))] })), sendButtonDisabled &&
                                !isReplyDraftMode &&
                                !isDraftingNewEmail &&
                                !isRewriteMode && (_jsx("div", { className: "flex justify-center py-4", children: _jsxs("div", { className: "flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full", children: [_jsx(LoadingSpinner, {}), _jsx("span", { className: "text-sm text-muted-foreground", children: "Analyzing email..." })] }) }))] }) }) }), !isDraftingNewEmail && (_jsx("div", { className: "flex-shrink-0 bg-background/80 backdrop-blur-sm border-t", children: _jsx(UserChatInput, { onMessageSend: onMessageSend, value: inputText, onChange: setInputText, sendButtonDisabled: sendButtonDisabled || !inputText.trim() }) }))] }));
};
export default OutlookChat;
